import axios from 'axios';
import { useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

export const collegeApi = axios.create({ baseURL: `${API}/api` });
collegeApi.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('upa_college_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const PINK = '#FF007F';
export const PURPLE = '#7000FF';
export const GOLD = '#FFD700';

export const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { setShown(true); return; }
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); obs.disconnect(); } }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={className} style={{
      opacity: shown ? 1 : 0,
      transform: shown ? 'translateY(0)' : 'translateY(28px)',
      transition: `opacity 0.8s ease ${delay}ms, transform 0.8s ease ${delay}ms`,
    }}>
      {children}
    </div>
  );
};

export const preloadCollegeAssets = () => {
  ['campus-exterior.jpg', 'campus-interior-studios.jpg', 'blackbox-theatre.jpg', 'creative-hub-atrium.jpg', 'aerial-production.jpg'].forEach((name) => {
    if (assetCache[name] || assetPending[name]) return;
    assetPending[name] = collegeApi.get(`/college/assets/${name}`, { responseType: 'blob' })
      .then(r => { assetCache[name] = URL.createObjectURL(r.data); return assetCache[name]; })
      .catch(() => { delete assetPending[name]; });
  });
};

const assetCache = {};
const assetPending = {};
export const ProtectedImg = ({ name, alt, className = '', style }) => {
  const [src, setSrc] = useState(assetCache[name] || null);
  useEffect(() => {
    if (assetCache[name]) { setSrc(assetCache[name]); return; }
    let alive = true;
    if (!assetPending[name]) {
      assetPending[name] = collegeApi.get(`/college/assets/${name}`, { responseType: 'blob' })
        .then(r => { assetCache[name] = URL.createObjectURL(r.data); return assetCache[name]; })
        .catch(() => { delete assetPending[name]; });
    }
    assetPending[name].then((url) => { if (alive && url) setSrc(url); });
    return () => { alive = false; };
  }, [name]);
  if (!src) return <div className={`${className} bg-gradient-to-br from-[#14121c] to-[#0a090e]`} style={style} aria-hidden="true" />;
  return <img src={src} alt={alt} className={className} style={style} decoding="async" />;
};

export const MotionStyles = () => (
  <style>{`
    @keyframes college-kenburns { 0% { transform: scale(1) translateY(0); } 100% { transform: scale(1.09) translateY(-1.5%); } }
    @keyframes college-aurora { 0% { transform: translate(0,0) scale(1); } 50% { transform: translate(4%, -3%) scale(1.12); } 100% { transform: translate(-3%, 2%) scale(1.04); } }
    @keyframes college-aurora2 { 0% { transform: translate(0,0) scale(1.05); } 50% { transform: translate(-5%, 4%) scale(1); } 100% { transform: translate(3%, -2%) scale(1.1); } }
    @keyframes college-spin { to { transform: rotate(360deg); } }
    @keyframes college-pulse { 0%,100% { box-shadow: 0 0 50px rgba(112,0,255,0.35); } 50% { box-shadow: 0 0 90px rgba(255,0,127,0.45); } }
    @keyframes college-dotpulse { 0%,100% { box-shadow: 0 0 0 0 rgba(255,0,127,0.5); } 50% { box-shadow: 0 0 0 7px rgba(255,0,127,0); } }
    @keyframes college-golddot { 0%,100% { box-shadow: 0 0 0 0 rgba(255,215,0,0.5); } 50% { box-shadow: 0 0 0 7px rgba(255,215,0,0); } }
    @keyframes college-shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
    @keyframes college-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }
    .college-kenburns { animation: college-kenburns 24s ease-in-out infinite alternate; will-change: transform; }
    .college-aurora { animation: college-aurora 18s ease-in-out infinite alternate; }
    .college-aurora2 { animation: college-aurora2 22s ease-in-out infinite alternate; }
    .college-orbit { animation: college-spin 70s linear infinite; }
    .college-core-pulse { animation: college-pulse 5s ease-in-out infinite; }
    .college-dot-pulse { animation: college-dotpulse 2.6s ease-out infinite; }
    .college-gold-pulse { animation: college-golddot 2.6s ease-out infinite; }
    .college-shimmer {
      background: linear-gradient(100deg, #FFD700 35%, #fff7c2 48%, #FFD700 60%);
      background-size: 200% auto; -webkit-background-clip: text; background-clip: text;
      -webkit-text-fill-color: transparent; animation: college-shimmer 5.5s linear infinite;
    }
    .college-shimmer-pink {
      background: linear-gradient(100deg, #FF007F 35%, #ff7ab8 48%, #FF007F 60%);
      background-size: 200% auto; -webkit-background-clip: text; background-clip: text;
      -webkit-text-fill-color: transparent; animation: college-shimmer 6.5s linear infinite;
    }
    .college-float { animation: college-float 5s ease-in-out infinite; }
    .college-card { transition: transform 0.45s cubic-bezier(0.22,1,0.36,1), box-shadow 0.45s ease, border-color 0.3s ease; }
    .college-card:hover { transform: translateY(-5px); box-shadow: 0 18px 50px rgba(0,0,0,0.5), 0 0 30px rgba(112,0,255,0.12); }
    .college-fadeimg { transition: opacity 0.6s ease; }
    @media (prefers-reduced-motion: reduce) {
      .college-kenburns, .college-aurora, .college-aurora2, .college-orbit, .college-core-pulse,
      .college-dot-pulse, .college-gold-pulse, .college-shimmer, .college-shimmer-pink, .college-float { animation: none !important; }
      .college-card:hover { transform: none; }
    }
  `}</style>
);

export const Aurora = () => (
  <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
    <div className="college-aurora absolute -top-[20%] -right-[15%] w-[70vw] h-[70vw] rounded-full"
      style={{ background: 'radial-gradient(circle, rgba(112,0,255,0.12), transparent 65%)', willChange: 'transform' }} />
    <div className="college-aurora2 absolute top-[45%] -left-[20%] w-[60vw] h-[60vw] rounded-full"
      style={{ background: 'radial-gradient(circle, rgba(255,0,127,0.08), transparent 65%)', willChange: 'transform' }} />
  </div>
);

export const CountUp = ({ value, prefix = '', suffix = '', duration = 1800, className = '', style }) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { setDisplay(value); return; }
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        setDisplay(Math.round(value * eased));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [value, duration]);
  return <span ref={ref} className={className} style={style}>{prefix}{display.toLocaleString('en-GB')}{suffix}</span>;
};

export const Eyebrow = ({ children, color = PINK }) => (
  <p className="text-xs sm:text-sm uppercase tracking-[0.3em] mb-4 font-semibold" style={{ color }}>{children}</p>
);

export const SectionTitle = ({ children }) => (
  <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white leading-tight mb-6">{children}</h2>
);

export const Glass = ({ children, className = '', ...rest }) => (
  <div {...rest} className={`college-card rounded-2xl border border-white/10 p-6 sm:p-8 ${className}`}
    style={{ background: 'linear-gradient(145deg, rgba(20,18,28,0.85), rgba(10,9,14,0.9))' }}>{children}</div>
);

export const Disclaimer = ({ children }) => (
  <p className="text-xs text-white/40 leading-relaxed border-l-2 border-[#FFD700]/40 pl-3 mt-6">{children}</p>
);

export const SectionShell = ({ id, children, className = '', ...rest }) => (
  <section {...rest} id={id} data-college-section={id} className={`relative px-6 sm:px-10 lg:px-24 py-20 sm:py-28 max-w-[1400px] mx-auto ${className}`}>
    {children}
  </section>
);
